import {useCallback, useState} from 'react';
import {DataTableSortState} from '@carbon/react';
import {SortOrderItem} from './types';

export const DEFAULT_SORT_DIRECTION: DataTableSortState = 'NONE';
const SORT_ORDER: DataTableSortState[] = ['NONE', 'ASC', 'DESC'];

/**
 * Custom hook for managing table sorting state with three-state cycling (ASC → DESC → NONE)
 *
 * @param initialSortState - Optional initial sort state with column name and direction
 * @returns Object containing sortState, sortRows function, and resetSort function
 *
 * @example
 * // Initialize with no sorting
 * const {sortState, sortRows} = useSorting();
 *
 * @example
 * // Initialize with ASC sorting on 'name' column
 * const {sortState, sortRows} = useSorting({column_name: 'name', direction: 'ASC'});
 *
 * @example
 * // Initialize with DESC sorting on 'date' column
 * const {sortState, sortRows} = useSorting({column_name: 'date', direction: 'DESC'});
 */
export function useSorting(initialSortState?: SortOrderItem): {
    sortState: SortOrderItem | null;
    sortRows: (column_name: string) => () => void;
    resetSort: () => void;
} {
    const [sortState, setSortState] = useState<SortOrderItem | null>(initialSortState || null);

    const sortRows = useCallback((column_name: string) => {
        return () => {
            setSortState((currentState) => {
                if (!currentState || currentState.column_name !== column_name) {
                    // Start sorting by this column in ASC order
                    return {direction: 'ASC', column_name};
                }

                // Cycle through: ASC -> DESC -> NONE
                const currentIndex = SORT_ORDER.findIndex(
                    (order) => order === currentState.direction
                );
                const nextDirection = SORT_ORDER[(currentIndex + 1) % SORT_ORDER.length];

                if (nextDirection === 'NONE') {
                    // Reset to no sorting
                    return null;
                }

                return {direction: nextDirection, column_name};
            });
        };
    }, []);

    const resetSort = useCallback(() => {
        setSortState(null);
    }, []);

    return {sortState, sortRows, resetSort};
}

export {SORT_ORDER};
